import * as React from "react";
import { BadgeCheck, Globe, Mail, MapPin, Phone, Store } from "lucide-react";
import { Button, Card, useToast } from "randmarcomps";
import Reseller from "../../stores/reseller/Reseller";

const formatLocation = (city?: string, province?: string, country?: string) =>
  [city, province, country].filter(Boolean).join(", ");

const normalizeUrl = (url?: string) => {
  if (!url) return undefined;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

type ResellerStatusPill = {
  id: string;
  label: string;
  className: string;
  Icon?: React.ComponentType<{ className?: string }>;
};

function buildStatusPills(reseller: Reseller): ResellerStatusPill[] {
  const pills: ResellerStatusPill[] = [];

  if (reseller.isQualified) {
    pills.push({
      id: "qualified",
      label: "Qualified",
      className: "border-emerald-200 bg-emerald-50 text-emerald-700",
      Icon: BadgeCheck,
    });
  }

  if (reseller.onHold) {
    pills.push({
      id: "on-hold",
      label: "On hold",
      className: "border-amber-200 bg-amber-50 text-amber-800",
    });
  } else {
    pills.push({
      id: "active",
      label: "Active",
      className: "border-slate-200 bg-slate-50 text-slate-700",
    });
  }

  if (reseller.priority) {
    pills.push({
      id: "priority",
      label: reseller.priority,
      className: "border-sky-200 bg-sky-50 text-sky-700",
    });
  }

  return pills;
}

interface ResellerProfileCardProps {
  reseller: Reseller;
  actions?: React.ReactNode;
  footer?: React.ReactNode;
}

const ResellerProfileCard: React.FC<ResellerProfileCardProps> = ({ reseller, actions, footer }) => {
  const { toast } = useToast();
  const [copiedField, setCopiedField] = React.useState<string | null>(null);
  const copyResetRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (copyResetRef.current) {
        window.clearTimeout(copyResetRef.current);
      }
    };
  }, []);

  const resellerId = reseller.resellerId ?? "";
  const location = formatLocation(reseller.city, reseller.province, reseller.country);
  const website = normalizeUrl(reseller.website);
  const pills = React.useMemo(() => buildStatusPills(reseller), [reseller]);

  const handleCopy = React.useCallback(
    async (label: string, value?: string) => {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        setCopiedField(label);
        toast({ variant: "success", title: "Copied", description: `${label} copied to clipboard.` });
      } catch {
        toast({ variant: "destructive", title: "Copy failed", description: `Couldn't copy ${label}.` });
      } finally {
        if (copyResetRef.current) {
          window.clearTimeout(copyResetRef.current);
        }
        copyResetRef.current = window.setTimeout(() => setCopiedField(null), 1500);
      }
    },
    [toast]
  );

  return (
    <Card className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-4">
          <div className="flex-shrink-0">
            {resellerId ? (
              <img
                src={`https://api.randmar.io/V4/Partner/${resellerId}/Account/Logo?width=144`}
                alt={`${reseller.publicName || reseller.name || "Reseller"} logo`}
                loading="lazy"
                className="h-14 w-14 rounded-xl border border-gray-100 bg-white object-contain p-2"
              />
            ) : (
              <div className="grid h-14 w-14 place-items-center rounded-xl border border-gray-100 bg-gray-50">
                <Store className="h-6 w-6 text-slate-400" aria-hidden="true" />
              </div>
            )}
          </div>

          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reseller profile</p>
            <h1 className="mt-2 text-[32px] font-semibold leading-tight text-slate-900">
              {reseller.publicName || reseller.name || "Reseller"}
            </h1>
            {reseller.name && reseller.publicName && reseller.name !== reseller.publicName && (
              <p className="mt-1 text-[13px] text-slate-500">{reseller.name}</p>
            )}
            {location && (
              <p className="mt-2 inline-flex items-center gap-2 text-[15px] text-slate-500">
                <MapPin className="h-4 w-4" aria-hidden="true" />
                <span className="min-w-0 truncate">{location}</span>
              </p>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              {pills.map(({ id, label, className, Icon }) => (
                <span
                  key={id}
                  className={[
                    "inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide",
                    className,
                  ].join(" ")}
                >
                  {Icon ? <Icon className="h-4 w-4" aria-hidden="true" /> : null}
                  {label}
                </span>
              ))}
            </div>
          </div>
        </div>

        <dl className="grid gap-3 sm:min-w-[320px]">
          {actions ? <div className="flex justify-start sm:justify-end">{actions}</div> : null}
          {reseller.contactName && (
            <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Point of contact</dt>
              <dd className="mt-1 text-[15px] font-medium text-slate-900">{reseller.contactName}</dd>
            </div>
          )}

          {reseller.publicEmail && (
            <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</dt>
              <dd className="mt-2 flex flex-wrap items-center gap-2">
                <a
                  href={`mailto:${reseller.publicEmail}`}
                  className="inline-flex items-center gap-2 text-[15px] font-medium text-[color:var(--ops-accent)] hover:underline"
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  {reseller.publicEmail}
                </a>
              </dd>
            </div>
          )}

          {reseller.phone && (
            <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Phone</dt>
              <dd className="mt-2 flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2 text-[15px] font-medium text-slate-900">
                  <Phone className="h-4 w-4 text-slate-400" aria-hidden="true" />
                  {reseller.phone}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-xl"
                  onClick={() => handleCopy("Phone", reseller.phone)}
                >
                  {copiedField === "Phone" ? "Copied" : "Copy"}
                </Button>
              </dd>
            </div>
          )}

          {website && (
            <div className="rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Website</dt>
              <dd className="mt-2">
                <a
                  href={website}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-[15px] font-medium text-[color:var(--ops-accent)] hover:underline"
                >
                  <Globe className="h-4 w-4" aria-hidden="true" />
                  {website.replace(/^https?:\/\//i, "")}
                </a>
              </dd>
            </div>
          )}
        </dl>
      </div>

      {footer ? <div className="mt-6 border-t border-gray-100 pt-4">{footer}</div> : null}
    </Card>
  );
};

export default ResellerProfileCard;
